import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, LogOut, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import AdminLoginCard from '../components/sections/AdminLoginCard';
import BlogPostRow, { computeStatus } from '../components/sections/BlogPostRow';
import { useAdminSession } from '../hooks/useAdminSession';
import { HTTP } from '../lib/httpStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBlog = () => {
  const { isLoggedIn, login, logout, authedFetch } = useAdminSession();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadPosts = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await authedFetch({
        url: `${BACKEND_URL}/api/admin/blog/posts`,
        method: 'GET',
      });
      setPosts(data.posts || []);
    } catch (error) {
      if (error.response?.status !== HTTP.UNAUTHORIZED) toast.error('Failed to load posts');
    } finally {
      setIsLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    if (isLoggedIn) loadPosts();
  }, [isLoggedIn, loadPosts]);

  if (!isLoggedIn) {
    return (
      <AdminLoginCard
        onLogin={login}
        subtitle="Enter the admin password to manage the content calendar."
      />
    );
  }

  const counts = posts.reduce(
    (acc, p) => {
      const s = computeStatus(p);
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    { published: 0, scheduled: 0, draft: 0 }
  );

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 rounded-full p-3">
              <CalendarDays className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Content Calendar</h1>
              <p className="text-sm text-gray-600">
                {counts.published} published · {counts.scheduled} scheduled · {counts.draft} drafts
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Link to="/admin/submissions">
              <Button variant="outline">Submissions</Button>
            </Link>
            <Button variant="outline" onClick={loadPosts} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>

        {isLoading && posts.length === 0 && (
          <p className="text-center text-gray-500 py-10">Loading posts…</p>
        )}

        {!isLoading && posts.length === 0 && (
          <Card className="border-2">
            <CardContent className="p-10 text-center text-gray-500">
              No posts yet.
            </CardContent>
          </Card>
        )}

        <div className="space-y-3" data-testid="admin-blog-list">
          {posts.map((p) => (
            <BlogPostRow
              key={p.id}
              post={p}
              authedFetch={authedFetch}
              onUpdated={loadPosts}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminBlog;
